import { Injectable, inject, WritableSignal } from '@angular/core';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { AccountService, SubscriptionType } from '../account/account.service';
import { WaypointService } from '../core/waypoint.service';
import { StackedModalsService } from '../modal/stacked-modals.service';
import { LanguageService } from '../account/language.service';
import { MapService } from '../map/map.service';
import { DisplayMode } from '../../models/display-mode';
import { LatLng } from '../../models/latlng';
import { ECarSettings } from '../../models/ecar';
import { ConfirmationDialogComponent } from '../../components/modals/confirmation-dialog/confirmation-dialog.component';
import { AccountDialogComponent } from '../../components/modals/account-dialog/account-dialog.component';
import { VehicleOptionsDialogComponent } from '../../components/modals/vehicle-options-dialog/vehicle-options-dialog.component';
import { PrivacyPolicyComponent } from '../../components/modals/info/privacy-policy/privacy-policy.component';
import { SubscriptionSuccessDialogComponent } from '../../components/modals/subscriptions-dialog/subscription-success-dialog/subscription-success-dialog.component';
import { InfoDialogComponent } from '../../components/modals/info-dialog/info-dialog.component';
import { VehicleService } from '../vehicle/vehicle.service';
import { WidgetService } from '../platform/widget.service';

/**
 * MainComponent implements this and passes itself to bind().
 * The handler sets the two flags that feed into the component template.
 */
export interface RouterFlowHost {
  readonly demo: WritableSignal<boolean>;
  readonly iframeRedirect: WritableSignal<boolean>;
}

/**
 * Owns the router-events subscription that handles all deep-link
 * query parameters (demo mode, Apple auth, vouchers, subscription
 * success/failure, carid iframe, email verification).
 *
 * Split from MainComponent because the ~130-line subscription is
 * self-contained and only needs injectable services plus two writable
 * host flags.
 */
@Injectable({ providedIn: 'root' })
export class RouterFlowHandler {
  private host: RouterFlowHost | null = null;
  private sub: Subscription | null = null;
  private carSelectorSub: Subscription | null = null;
  // Guards against stacking multiple first-vehicle dialogs while one is already open.
  private firstVehicleDialogOpen = false;

  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly accountService = inject(AccountService);
  private readonly waypointService = inject(WaypointService);
  private readonly mapService = inject(MapService);
  private readonly stackedModalsService = inject(StackedModalsService);
  private readonly languageService = inject(LanguageService);
  private readonly vehicleService = inject(VehicleService);
  private readonly widgetService = inject(WidgetService);

  bind(host: RouterFlowHost): void {
    this.host = host;
    if (!this.sub) {
      this.sub = this.router.events.subscribe(value => {
        if (value instanceof NavigationEnd) this.handleNavEnd(value);
      });
    }
    // An authorized user must always have at least one vehicle. getUserVehicles()
    // emits here whenever the user's vehicle list comes back empty (on startup via
    // the init resolver, after email verification, or after the user deletes their
    // last car), so we keep (re)showing the non-dismissable vehicle dialog until a
    // vehicle is actually saved. This survives the user closing/refreshing the page,
    // which a "save on dismiss" handler never could.
    if (!this.carSelectorSub) {
      this.carSelectorSub = this.accountService.ObservableOpenCarSelectorDialog.subscribe(open => {
        if (open) this.openFirstVehicleDialog();
      });
    }
  }

  unbind(): void {
    this.sub?.unsubscribe();
    this.sub = null;
    this.carSelectorSub?.unsubscribe();
    this.carSelectorSub = null;
    this.host = null;
  }

  // Opens the vehicle dialog locked to the "first vehicle" mode: no Cancel button
  // (hidden in the template via firstUserCar) and disableClose so ESC/backdrop can't
  // dismiss it. The only way out is saving a vehicle via OK.
  private openFirstVehicleDialog(): void {
    if (this.firstVehicleDialogOpen || !this.accountService.getIsAuthorized()) return;
    this.firstVehicleDialogOpen = true;
    const modalRef = this.stackedModalsService.openModal(VehicleOptionsDialogComponent, {
      data: { firstUserCar: true },
      autoFocus: false,
      disableClose: true,
      panelClass: 'dialog-snap-to-fullscreen',
    });
    modalRef.afterClosed().subscribe(() => { this.firstVehicleDialogOpen = false; });
  }

  // Widget `mode`, `lat`/`lon` and `zoom` query parameters.
  private applyWidgetStart(): void {
    const mode = this.widgetService.getMode();
    if (mode) {
      this.waypointService.setSelectedDisplayMode(mode === 'route' ? DisplayMode.ROUTE : DisplayMode.RANGE);
    }
    const start = this.widgetService.getStartPosition();
    if (start) {
      const waypoints = [new LatLng(start.lat, start.lng)];
      this.waypointService.setWaypointParams(waypoints);
      this.mapService.ObservableMoveTo.next({ latlng: waypoints[0], zoom: this.widgetService.getZoom() ?? 10 });
    }
  }

  private handleNavEnd(value: NavigationEnd): void {
    if (!this.host) return;

    // Widget mode handles only its own parameters; none of the deep links below
    // (auth, vouchers, subscriptions, carid iframe) apply to an embedded map.
    if (this.widgetService.isWidget()) {
      this.applyWidgetStart();
      return;
    }

    if (this.activatedRoute?.snapshot?.routeConfig?.path === 'demo') {
      this.host.demo.set(true);
      document.body.classList.add('demo');
    }

    if (value.url.length > 1 && value.url.includes('privacy-policy')) {
      this.stackedModalsService.openModal(PrivacyPolicyComponent, {
        panelClass: 'dialog-snap-to-fullscreen',
        autoFocus: false,
      });
    }

    if (value.url.length > 1 && value.url.includes('apple_access_token')) {
      const urlParam = this.activatedRoute.snapshot.queryParamMap.get('apple_access_token');
      this.accountService.initSocialProfile({ success: { token: urlParam } }, 'apple').subscribe();
      this.accountService.accountInit = true;
      window.history.pushState('', '', '/');
    }

    if (value.url.length > 1 && value.url.includes('voucher_token')) {
      const voucherToken = this.activatedRoute.snapshot.queryParamMap.get('voucher_token') ?? '';
      window.history.pushState('', '', '/');
      this.accountService.voucherCheck(voucherToken).subscribe(resp => {
        if (resp.success === 'VOUC_MSG_001' && this.accountService.getIsAuthorized()) {
          this.accountService.addVoucherSubscription(voucherToken).subscribe();
        } else if (resp.success) {
          localStorage.setItem('voucherToken', voucherToken);
        }
      });
    }

    if (value.url.length > 1 && value.url.includes('subscription-success')) {
      const subscriptionType = this.activatedRoute.snapshot.queryParamMap.get('package');
      const subscriptionPeriod = this.activatedRoute.snapshot.queryParamMap.get('period');
      this.stackedModalsService.openModal(SubscriptionSuccessDialogComponent, {
        data: { subscriptionType: `evnavigation.${subscriptionType}.${subscriptionPeriod}` as SubscriptionType },
        autoFocus: false,
      });
      window.history.pushState('', '', '/');
    }

    if (value.url.length > 1 && value.url.includes('subscription-failure')) {
      setTimeout(() => {
        this.stackedModalsService.openModal(InfoDialogComponent, {
          panelClass: 'subscription-info',
          data: {
            title: this.languageService.languageJSON.Global_Error,
            message: this.languageService.languageJSON.AccountDialog_Verification_Error,
          },
          autoFocus: false,
        });
      });
      window.history.pushState('', '', '/');
    }

    if (value.url.length > 1 && value.url.includes('carid')) {
      this.host.iframeRedirect.set(true);
      const carId = parseInt(this.activatedRoute.snapshot.queryParamMap.get('carid') ?? '');
      const lat = parseFloat(this.activatedRoute.snapshot.queryParamMap.get('lat') ?? '');
      const lon = parseFloat(this.activatedRoute.snapshot.queryParamMap.get('lon') ?? '');

      if (!this.accountService.getIsAuthorized()) {
        const foundCar = this.vehicleService.getCarByID(carId);
        if (foundCar) {
          this.vehicleService.setSelectedCar(foundCar, true);
        }
        const carSettings: ECarSettings = {
          batteryLevel: 100,
          weight: 2,
          tirePressure: 2.2,
          userBehavior: 50,
          speedLimiter: 100,
          batterySafetyLimitRha: 50,
          batterySafetyLimitRoute: 30,
          minBatteryAtDestination: 10,
          batteryStateOfHealth: 100,
          fastChargeLimit: 90,
          batterySafetyLimit: 50,
        };
        this.vehicleService.setECarSettings(carSettings);
      }

      this.waypointService.setSelectedDisplayMode(DisplayMode.RANGE);
      const waypoints = [new LatLng(lat, lon)];
      this.waypointService.setWaypointParams(waypoints);
      this.mapService.ObservableMoveTo.next({ latlng: waypoints[0], zoom: 7 });
    }

    if (value.url.length > 1 && value.url.includes('apple_error')) {
      this.stackedModalsService.openModal(ConfirmationDialogComponent, {
        data: {
          title: this.languageService.languageJSON.App_LoginFailed,
          message: this.languageService.languageJSON.App_LoginFailed_Description,
          buttons: [this.languageService.languageJSON.Global_Ok, null],
        },
        autoFocus: false,
      });
      window.history.pushState('', '', '/');
    }

    if (value.url.length > 1 && value.url.includes('verify-email')) {
      const urlParam = this.activatedRoute.snapshot.queryParamMap.get('queryURL') ?? '';
      this.accountService.verifyEmail(urlParam).subscribe({
        next: resp => {
          if (resp.success) {
            this.stackedModalsService.openModal(AccountDialogComponent, {
              data: { process: 'verifySuccess' },
              autoFocus: false,
              panelClass: 'dialog-snap-to-fullscreen',
            });
            // No explicit open here: getUserVehicles() emits ObservableOpenCarSelectorDialog
            // when the list is empty, which the bind() subscription turns into the dialog.
            this.accountService.getUserVehicles().subscribe();
            this.accountService.initUserData(true, true, false).subscribe();
          } else {
            this.stackedModalsService.openModal(AccountDialogComponent, {
              data: { process: 'verifyFailed' },
              autoFocus: false,
              panelClass: 'dialog-snap-to-fullscreen',
            });
          }
        },
        error: () => {
          this.stackedModalsService.openModal(AccountDialogComponent, {
            data: { process: 'verifyFailed' },
            autoFocus: false,
            panelClass: 'dialog-snap-to-fullscreen',
          });
        },
      });
    }
  }
}
